import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PxWindow, ChipLogo } from '../components/PixelUI';
import { getSessionId, streamByte, getByteHistory, mkMsgId } from '../api';
import { devWarn } from '../lib/log';
import { BYTE_SUGGESTIONS } from '../catalog';

const MODEL_TAG = 'GPT-5.4 · WIRE IN HAND';

const ChatMessage = ({ msg }) => (
  <div className={`nv-msg ${msg.role}`}>
    {msg.text || 'checking the wire…'}
    {msg.role === 'bot' && <span className="meta">{MODEL_TAG}</span>}
  </div>
);

const AgentHeader = () => (
  <>
    <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginBottom: 8 }}>
      <div style={{ background: 'var(--nv-brown)', border: '3px solid var(--nv-ink)', padding: 8, boxShadow: '4px 4px 0 rgba(0,0,0,.3)' }}>
        <ChipLogo size={40} />
      </div>
      <div>
        <p style={{ margin: 0, fontSize: 11, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--nv-cream)' }}>the console is live</p>
        <h1 className="font-pixel" style={{ fontSize: 'clamp(24px, 5vw, 44px)', color: 'var(--nv-gold)', textShadow: '3px 3px 0 var(--nv-ink)', margin: '6px 0 0', letterSpacing: 3 }} data-testid="agent-title">
          ASK BYTE
        </h1>
      </div>
    </div>
    <p style={{ color: 'var(--nv-cream-2)', fontSize: 13, marginBottom: 26, lineHeight: 1.7 }}>
      byte reads the live wire before every reply — and it is hard-wired against making numbers up
    </p>
  </>
);

// Chat state + streaming logic as a small custom hook
const useByteChat = () => {
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [booted, setBooted] = useState(false);

  const appendDelta = useCallback((delta) => {
    setMessages((m) => {
      const copy = [...m];
      const last = copy[copy.length - 1];
      copy[copy.length - 1] = { ...last, text: last.text + delta };
      return copy;
    });
  }, []);

  const ask = useCallback(async (raw) => {
    const question = raw.trim();
    if (!question) return;
    setBusy(true);
    setMessages((m) => [...m,
      { id: mkMsgId(), role: 'user', text: question },
      { id: mkMsgId(), role: 'bot', text: '' },
    ]);
    try {
      await streamByte(getSessionId(), question, appendDelta);
    } catch (err) {
      devWarn('[nanovram] byte stream failed:', err?.message);
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { ...copy[copy.length - 1], text: 'byte is offline — give it a minute and retry' };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  }, [appendDelta]);

  const boot = useCallback(async () => {
    const hist = await getByteHistory(getSessionId());
    if (hist.length) {
      setMessages(hist.map((m) => ({ id: mkMsgId(), role: m.role, text: m.text })));
    } else {
      setMessages([{ id: mkMsgId(), role: 'bot', text: 'byte online. ask me anything the wire carries.' }]);
    }
    setBooted(true);
  }, []);

  return { messages, busy, booted, ask, boot };
};

const Agent = () => {
  const [searchParams] = useSearchParams();
  const [input, setInput] = useState('');
  const { messages, busy, booted, ask, boot } = useByteChat();
  const bodyRef = useRef(null);
  const bootedOnce = useRef(false);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, busy]);

  // Mount-only boot: load persisted history, then fire ?q= if present.
  useEffect(() => {
    if (bootedOnce.current) return;
    bootedOnce.current = true;
    (async () => {
      await boot();
      const q = searchParams.get('q');
      if (q) ask(q);
    })();
  }, [boot, ask, searchParams]);

  const submit = () => {
    if (busy) return;
    setInput('');
    ask(input);
  };

  return (
    <main className="bg-ground-band" style={{ borderTop: 'none', minHeight: '85vh' }}>
      <section className="nv-section" style={{ paddingTop: 44 }}>
        <div className="nv-section-inner" style={{ maxWidth: 820 }}>
          <AgentHeader />

          <PxWindow title="BYTE.ROM" tape>
            <div className="nv-chat-body" ref={bodyRef} data-testid="agent-chat-body" style={{ minHeight: 340 }}>
              {!booted && <div className="nv-msg bot">powering the chip…</div>}
              {messages.map((m) => <ChatMessage key={m.id} msg={m} />)}
            </div>
            <div className="nv-chat-input">
              <input
                data-testid="agent-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && submit()}
                placeholder="ask the wire…"
                autoFocus
              />
              <button className="px-btn sm" onClick={submit} disabled={busy} data-testid="agent-send-btn">Send</button>
            </div>
          </PxWindow>

          <div className="nv-sugg" style={{ marginTop: 20 }}>
            {BYTE_SUGGESTIONS.map((s) => (
              <button key={s} onClick={() => !busy && ask(s)} data-testid={`sugg-${s.replaceAll(' ', '-')}`}>{s}</button>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default Agent;
